import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { feedbackService } from '../services/feedbackService';
import { CheckCircle, ArrowLeft, Send } from 'lucide-react';

interface VisibilityCondition {
  triggerQuestionName: string;
  operator: 'anyOf' | 'noneOf';
  values: string[];
}

interface Choice {
  value: string;
  text: string;
}

interface Question {
  id: string;
  name: string;
  text: string;
  type: string;
  required: boolean;
  choices?: Choice[];
  ratingScale?: number;
  visibilityCondition?: VisibilityCondition;
}

interface FeedbackData {
  id: string;
  title: string;
  description: string;
  questions: Question[];
  versionNo: number;
}

const FeedbackTaker: React.FC = () => {
  const { id, instanceId } = useParams<{ id: string; instanceId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const occurrenceId = searchParams.get('occurrenceId');

  const [feedback, setFeedback] = useState<FeedbackData | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadFeedback = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const basicFeedback = await feedbackService.getFeedbackById(id);
        if (!basicFeedback) {
          setLoading(false);
          return;
        }

        const versionNo = basicFeedback.currentVersion || 1;
        const structure = await feedbackService.getFeedbackStructure(id, versionNo);

        const allQuestions: Question[] = [];
        structure.pages.forEach(page => {
          page.questions.forEach(q => {
            const question: Question = {
              id: q.id || q.name,
              name: q.name,
              text: q.title,
              type: mapQuestionType(q.type),
              required: q.required,
            };

            if (q.choices && q.choices.length > 0) {
              question.choices = q.choices.map(c => ({ value: c.value, text: c.text }));
            }

            if (q.type === 'rating' && q.config) {
              question.ratingScale = (q.config as any).rateMax || 5;
            }

            if (q.config?.visibilityCondition) {
              question.visibilityCondition = q.config.visibilityCondition;
            }

            allQuestions.push(question);
          });
        });

        setFeedback({
          id: basicFeedback.id,
          title: basicFeedback.title || basicFeedback.name || 'Untitled Feedback',
          description: basicFeedback.description || '',
          questions: allQuestions,
          versionNo,
        });

        const initialAnswers: Record<string, string | string[]> = {};
        allQuestions.forEach((q) => {
          if (q.type === 'checkbox') {
            initialAnswers[q.id] = [];
          } else {
            initialAnswers[q.id] = '';
          }
        });
        setAnswers(initialAnswers);
      } catch (error) {
        console.error('Failed to load feedback structure:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFeedback();
  }, [id]);

  const mapQuestionType = (type: string): string => {
    const typeMap: Record<string, string> = {
      'text': 'text',
      'comment': 'text',
      'radiogroup': 'multipleChoice',
      'multiplechoice': 'multipleChoice',
      'checkbox': 'checkbox',
      'rating': 'rating',
      'dropdown': 'multipleChoice',
    };
    return typeMap[type.toLowerCase()] || 'text';
  };

  const isQuestionVisible = useCallback((question: Question): boolean => {
    if (!question.visibilityCondition || !feedback) return true;

    const { triggerQuestionName, operator, values } = question.visibilityCondition;
    const triggerQuestion = feedback.questions.find(q => q.name === triggerQuestionName);
    if (!triggerQuestion) return true;

    const answer = answers[triggerQuestion.id];
    if (!answer || (Array.isArray(answer) && answer.length === 0)) {
      return operator === 'noneOf';
    }

    const answerValues = Array.isArray(answer) ? answer : [answer];
    const hasMatch = answerValues.some(answerVal => values.includes(answerVal));

    return operator === 'anyOf' ? hasMatch : !hasMatch;
  }, [answers, feedback]);

  const handleAnswerChange = (questionId: string, value: string | string[]) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[questionId];
      return newErrors;
    });
  };

  const handleCheckboxChange = (questionId: string, option: string, checked: boolean) => {
    const currentAnswers = (answers[questionId] as string[]) || [];
    if (checked) {
      handleAnswerChange(questionId, [...currentAnswers, option]);
    } else {
      handleAnswerChange(
        questionId,
        currentAnswers.filter((a) => a !== option)
      );
    }
  };

  const validateAnswers = () => {
    if (!feedback) return false;

    const newErrors: Record<string, string> = {};
    let isValid = true;

    feedback.questions.forEach((q) => {
      if (!isQuestionVisible(q)) return;
      if (q.required) {
        const answer = answers[q.id];
        if (!answer || (Array.isArray(answer) && answer.length === 0)) {
          newErrors[q.id] = 'This field is required';
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateAnswers() || !feedback || !currentUser) {
      if (feedback) {
        const firstErrorId = feedback.questions.find(q => isQuestionVisible(q) && q.required && (!answers[q.id] || (Array.isArray(answers[q.id]) && (answers[q.id] as string[]).length === 0)))?.id;
        if (firstErrorId) {
          document.querySelector(`[data-question-id="${firstErrorId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      const answersByName: Record<string, string | string[]> = {};
      feedback.questions.forEach((q) => {
        if (!isQuestionVisible(q)) return;
        if (answers[q.id] !== undefined) {
          answersByName[q.name] = answers[q.id];
        }
      });

      const submitDto = {
        feedbackInstanceId: instanceId,
        feedbackOccurrenceId: occurrenceId || undefined,
        answers: answersByName,
      };

      await feedbackService.submitResponse(feedback.id, submitDto);

      queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'my-feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-instances'] });

      setSubmitted(true);
    } catch (error: any) {
      console.error('Failed to submit feedback response:', error);
      const message = error?.response?.status === 409
        ? 'You have already submitted a response for this feedback.'
        : error?.message || 'Something went wrong. Please try again later.';
      setErrors({ submit: message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">Loading feedback...</p>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">Feedback not found</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 text-center max-w-lg">
          <div className="flex justify-center mb-4">
            <div className="bg-green-100 dark:bg-green-900 p-4 rounded-full">
              <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Thank You!
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your response has been submitted successfully.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const renderQuestionInput = (question: Question) => {
    switch (question.type) {
      case 'text':
        return (
          <textarea
            value={(answers[question.id] as string) || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            rows={3}
            placeholder="Enter your answer"
          />
        );

      case 'multipleChoice':
        return (
          <div className="space-y-2">
            {question.choices?.map((choice) => (
              <label
                key={choice.value}
                className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-md"
              >
                <input
                  type="radio"
                  name={question.id}
                  value={choice.value}
                  checked={answers[question.id] === choice.value}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700 dark:text-gray-300">{choice.text}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {question.choices?.map((choice) => (
              <label
                key={choice.value}
                className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-md"
              >
                <input
                  type="checkbox"
                  value={choice.value}
                  checked={(answers[question.id] as string[])?.includes(choice.value)}
                  onChange={(e) => handleCheckboxChange(question.id, choice.value, e.target.checked)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700 dark:text-gray-300">{choice.text}</span>
              </label>
            ))}
          </div>
        );

      case 'rating': {
        const scale = question.ratingScale || 5;
        return (
          <div className="flex space-x-2">
            {Array.from({ length: scale }, (_, i) => i + 1).map((value) => (
              <button
                key={value}
                onClick={() => handleAnswerChange(question.id, value.toString())}
                className={`w-10 h-10 rounded-full transition-colors ${
                  answers[question.id] === value.toString()
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {value}
              </button>
            ))}
          </div>
        );
      }

      default:
        return null;
    }
  };

  let visibleIndex = 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/feedback/assigned')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{feedback.title}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">{feedback.description}</p>
        </div>
      </div>

      <div className="space-y-6">
        {feedback.questions.map((question) => {
          if (!isQuestionVisible(question)) return null;
          visibleIndex++;
          const currentNumber = visibleIndex;

          return (
            <div
              key={question.id}
              data-question-id={question.id}
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <div className="mb-4">
                <div className="flex items-start">
                  <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm mr-3 mt-0.5">
                    {currentNumber}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {question.text}
                      {question.required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </h3>
                  </div>
                </div>
              </div>

              {renderQuestionInput(question)}

              {errors[question.id] && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors[question.id]}</p>
              )}
            </div>
          );
        })}

        {errors.submit && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <button
            onClick={() => navigate('/feedback/assigned')}
            disabled={submitting}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Submit Feedback
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackTaker;
